import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { IoSearchSharp, IoClose } from 'react-icons/io5';
import { HiMicrophone } from 'react-icons/hi2';
import { FiClock, FiTrendingUp, FiMicOff } from 'react-icons/fi';
import { fetchProducts } from '../../api/catalog';

const FALLBACK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

/* ── Voice Search Hook ───────────────────────────────────── */
const useSpeechRecognition = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-IN'; // Indian English — best for Trozzi users
      recognition.maxAlternatives = 1;
      recognitionRef.current = recognition;
    }
  }, []);

  const startListening = useCallback((onResult, onEnd) => {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    setError('');
    setTranscript('');

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (e) => {
      let interim = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const text = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += text;
        else interim += text;
      }
      const current = final || interim;
      setTranscript(current);
      if (onResult) onResult(current, !!final);
    };

    recognition.onerror = (e) => {
      setIsListening(false);
      if (e.error === 'not-allowed' || e.error === 'permission-denied') {
        setError('Microphone access denied. Please allow mic permission.');
      } else if (e.error === 'no-speech') {
        setError('No speech detected. Try again.');
      } else if (e.error === 'network') {
        setError('Network error. Check your connection.');
      } else {
        setError('Voice search unavailable. Try again.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      if (onEnd) onEnd();
    };

    try {
      recognition.start();
    } catch (_e) {
      setIsListening(false);
    }
  }, []);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  return { isListening, transcript, error, supported, startListening, stopListening };
};

/* ── GlobalSearch Component ──────────────────────────────── */
const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const [micError, setMicError] = useState('');
  const navigate = useNavigate();
  const ref = useRef(null);
  const inputRef = useRef(null);

  const {
    isListening,
    error: speechError,
    supported: micSupported,
    startListening,
    stopListening,
  } = useSpeechRecognition();

  useEffect(() => {
    try {
      const saved = localStorage.getItem('fk_recent_searches');
      if (saved) setRecent(JSON.parse(saved));
    } catch (_e) {}
  }, []);

  // Clear mic error after 3s
  useEffect(() => {
    if (!speechError) return;
    setMicError(speechError);
    const t = setTimeout(() => setMicError(''), 3000);
    return () => clearTimeout(t);
  }, [speechError]);

  const saveRecent = (q) => {
    if (!q.trim()) return;
    const updated = [q, ...recent.filter((s) => s !== q)].slice(0, 6);
    setRecent(updated);
    try { localStorage.setItem('fk_recent_searches', JSON.stringify(updated)); } catch (_e) {}
  };

  // Debounced product search
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!query.trim()) { setResults([]); return; }
      setLoading(true);
      try {
        const data = await fetchProducts({ mode: 'public', q: query, limit: 8 });
        const items = Array.isArray(data) ? data : (data?.items || []);
        setResults(items.filter(Boolean));
      } catch (_e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    saveRecent(query.trim());
    navigate(`/ProductListing?q=${encodeURIComponent(query.trim())}`);
    setOpen(false);
    setQuery('');
  };

  const handleProductClick = (p) => {
    saveRecent(query);
    setOpen(false);
    setQuery('');
    const id = p?.id || p?._id;
    if (id) navigate(`/product/${id}`);
  };

  const handleRecentClick = (q) => {
    navigate(`/ProductListing?q=${encodeURIComponent(q)}`);
    setOpen(false);
    setQuery('');
  };

  /* ── Mic handler ── */
  const handleMicClick = () => {
    if (!micSupported) {
      setMicError('Voice search not supported in this browser.');
      setTimeout(() => setMicError(''), 3000);
      return;
    }

    if (isListening) {
      stopListening();
      return;
    }

    setOpen(true);
    setQuery('');
    inputRef.current?.focus();

    startListening(
      // onResult — update input live as user speaks
      (text, isFinal) => {
        setQuery(text);
        if (isFinal && text.trim()) {
          // Auto-search when speech is finalised
          saveRecent(text.trim());
          navigate(`/ProductListing?q=${encodeURIComponent(text.trim())}`);
          setOpen(false);
          setQuery('');
        }
      },
      // onEnd — if no final result yet, keep whatever was typed
      () => {}
    );
  };

  const showDropdown = open && (loading || results.length > 0 || recent.length > 0 || query.length === 0);

  return (
    <div className="relative w-full" ref={ref}>

      {/* ── Pill Search Input ── */}
      <form
        onSubmit={handleSubmit}
        className="flex items-center overflow-hidden transition-all duration-200"
        style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '99px',
          boxShadow: isListening
            ? '0 0 0 2.5px #ef4444, 0 4px 20px rgba(239,68,68,0.25)'
            : open
              ? '0 4px 20px rgba(37,99,235,0.18), 0 0 0 2px rgba(37,99,235,0.12)'
              : '0 2px 12px rgba(0,0,0,0.12)',
          border: '1px solid rgba(255,255,255,0.8)',
          outline: 'none',
          transition: 'box-shadow 0.25s ease',
        }}
      >
        {/* Search icon */}
        <button
          type="submit"
          className="flex-shrink-0 flex items-center justify-center w-10 h-10 text-slate-400 hover:text-emerald-600 transition-colors ml-1 outline-none focus:outline-none"
          aria-label="Search"
          tabIndex={-1}
        >
          <IoSearchSharp className="text-lg" />
        </button>

        {/* Text input */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={isListening ? 'Listening...' : 'Search products, brands...'}
          className="flex-1 py-2.5 text-[13px] text-slate-800 placeholder-slate-400 bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-none"
          style={{
            boxShadow: 'none',
            minWidth: 0,
            WebkitAppearance: 'none',
            color: isListening ? '#ef4444' : undefined,
          }}
        />

        {/* Right action buttons */}
        <div className="flex items-center gap-1 mr-2">
          {/* Clear button */}
          <AnimatePresence>
            {query && !isListening && (
              <motion.button
                type="button"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => { setQuery(''); setResults([]); inputRef.current?.focus(); }}
                className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:bg-slate-200 transition-colors outline-none focus:outline-none"
              >
                <IoClose className="text-sm" />
              </motion.button>
            )}
          </AnimatePresence>

          {/* Mic button */}
          <motion.button
            type="button"
            onClick={handleMicClick}
            whileTap={{ scale: 0.88 }}
            className="relative w-8 h-8 rounded-full flex items-center justify-center transition-all outline-none focus:outline-none"
            style={{
              background: isListening
                ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                : 'transparent',
              color: isListening ? '#fff' : '#94a3b8',
            }}
            aria-label={isListening ? 'Stop voice search' : 'Start voice search'}
            title={
              !micSupported
                ? 'Voice search not supported'
                : isListening
                  ? 'Tap to stop'
                  : 'Search by voice'
            }
          >
            {/* Pulse ring while listening */}
            {isListening && (
              <>
                <motion.span
                  className="absolute inset-0 rounded-full bg-red-400"
                  animate={{ scale: [1, 1.6, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.span
                  className="absolute inset-0 rounded-full bg-red-300"
                  animate={{ scale: [1, 1.9, 1], opacity: [0.3, 0, 0.3] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
                />
              </>
            )}

            {/* Icon */}
            {!micSupported ? (
              <FiMicOff className="text-[15px] relative z-10" />
            ) : (
              <HiMicrophone className="text-[15px] relative z-10" />
            )}
          </motion.button>
        </div>
      </form>

      {/* ── Mic error toast ── */}
      <AnimatePresence>
        {micError && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="absolute top-full left-0 right-0 mt-2 px-4 py-2.5 text-[12px] font-medium text-white rounded-2xl z-[600] flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 4px 16px rgba(239,68,68,0.3)' }}
          >
            <FiMicOff className="text-sm flex-shrink-0" />
            {micError}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Listening overlay in dropdown ── */}
      <AnimatePresence>
        {isListening && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 z-[500] px-5 py-6 text-center"
            style={{
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.12)',
              border: '1px solid #f1f5f9',
            }}
          >
            {/* Animated mic icon */}
            <div className="relative w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <motion.div
                className="absolute inset-0 rounded-full bg-red-100"
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute inset-2 rounded-full bg-red-200"
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut', delay: 0.15 }}
              />
              <div className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                <HiMicrophone className="text-white text-xl" />
              </div>
            </div>

            <p className="text-[15px] font-bold text-slate-800 mb-1">Listening...</p>
            <p className="text-[12px] text-slate-400 mb-3">Speak now to search</p>

            {/* Live transcript */}
            {query && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="px-4 py-2 rounded-2xl text-[14px] font-semibold text-slate-700 mb-3"
                style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
              >
                "{query}"
              </motion.div>
            )}

            {/* Sound wave bars */}
            <div className="flex items-end justify-center gap-1 h-8">
              {[0.4, 0.7, 1, 0.8, 0.5, 0.9, 0.6, 1, 0.7, 0.4].map((h, i) => (
                <motion.div
                  key={i}
                  className="w-1 rounded-full"
                  style={{ background: 'linear-gradient(180deg, #ef4444, #16a34a)' }}
                  animate={{ scaleY: [h * 0.4, h, h * 0.4] }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.07,
                  }}
                  initial={{ height: '100%', scaleY: h * 0.4 }}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={stopListening}
              className="mt-4 text-[12px] font-semibold text-slate-400 hover:text-red-500 transition-colors"
            >
              Tap to cancel
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Search Dropdown ── */}
      <AnimatePresence>
        {showDropdown && !isListening && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute top-full left-0 right-0 mt-2 z-[500] overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.08)',
              border: '1px solid rgba(241,245,249,0.8)',
              maxHeight: '420px',
              overflowY: 'auto',
            }}
          >
            {loading && (
              <div className="flex items-center gap-3 px-4 py-4 text-sm text-slate-500">
                <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                Searching...
              </div>
            )}

            {!loading && results.length > 0 && (
              <>
                <div className="px-4 pt-3 pb-1">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Products</p>
                </div>
                {results.map((p) => (
                  <div
                    key={p.id || p._id}
                    onClick={() => handleProductClick(p)}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      <img
                        src={p.image || FALLBACK_IMAGE}
                        alt={p.name}
                        className="w-full h-full object-contain"
                        onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-slate-800 truncate font-medium">{p.name}</p>
                      <p className="text-[12px] font-bold text-emerald-600">₹{Number(p.price || 0).toLocaleString()}</p>
                    </div>
                    <IoSearchSharp className="text-slate-300 flex-shrink-0 text-sm" />
                  </div>
                ))}
                <button
                  onClick={handleSubmit}
                  className="w-full px-4 py-3 text-[13px] font-semibold text-emerald-600 text-center hover:bg-emerald-50 transition-colors border-t border-slate-100"
                >
                  See all results for "{query}" →
                </button>
              </>
            )}

            {!loading && query.length === 0 && recent.length > 0 && (
              <>
                <div className="px-4 pt-3 pb-1 flex items-center gap-2">
                  <FiClock className="text-slate-400 text-sm" />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Recent Searches</p>
                </div>
                {recent.map((q, i) => (
                  <div
                    key={i}
                    onClick={() => handleRecentClick(q)}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <div className="w-7 h-7 rounded-xl bg-slate-100 flex items-center justify-center">
                      <FiClock className="text-slate-400 text-xs" />
                    </div>
                    <span className="text-[13px] text-slate-700">{q}</span>
                  </div>
                ))}
              </>
            )}

            {!loading && query.length === 0 && recent.length === 0 && (
              <div className="px-4 py-4">
                <div className="flex items-center gap-2 mb-3">
                  <FiTrendingUp className="text-emerald-500 text-sm" />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Trending</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {['Fashion', 'Electronics', 'Jewellery', 'Footwear', 'Beauty', 'Home Decor'].map((term) => (
                    <button
                      key={term}
                      onClick={() => handleRecentClick(term)}
                      className="px-3 py-1.5 text-[12px] font-medium rounded-full transition-all hover:scale-105 outline-none focus:outline-none"
                      style={{
                        background: 'linear-gradient(135deg, #eef4ff, #f5f3ff)',
                        color: '#0b4334',
                        border: '1px solid #dce9ff',
                      }}
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!loading && query.length > 0 && results.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-2xl mb-2">🔍</p>
                <p className="text-[13px] text-slate-500">No results for "<strong>{query}</strong>"</p>
                <p className="text-[11px] text-slate-400 mt-1">Try different keywords</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GlobalSearch;
